'use client';

import { base64Image, Default_Image } from '@/constants';
import Image from 'next/image';
import { useGlobalAdPopUp, useHomepageSections } from '@/hooks/homepage';

import { CollectionTabProperty, ISliderProperty } from '@/interfaces/homepage';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { IProductListPayload } from '@/interfaces/product';
import { IStore } from '@/interfaces/location';
import { useMemberExclusive, useProducts } from '@/hooks/product';
import { DeviceType, HomepageSections } from '@/enums';
import { usePathname, useRouter } from 'next/navigation';
import ImageSlider from '@/components/ImageSlider';
import PreBanner from '@/components/PreBanner';
import CategoryCarousel from '@/components/CategoryCarousel';
import SpecificProductSlider from '@/components/SpecificProductSlider';
import MultiColumnBanner from '@/components/MultiColumnBanner';
import Widgets from '@/components/Widgets';
import CustomTab from '@/components/CustomTab';
import CustomLink from '@/components/CustomLink';
import CustomButtonGroupAsArrows from '@/components/CustomButtonGroupAsArrows';
import ProductCardSkeleton from '@/components/ProductCardSkeleton';
import AuthProductSlider from '@/components/AuthProductSlider';
import ProductSlider from '@/components/ProductSlider';
import LoadingComponent from '@/components/LoadingComponent';
import Popup from '@/components/Popup';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import CloseHomePageClientComponent from '@/components/ClientHomePageComponent';
import {
  getKeyBannerInfo,
  homepageCollectionItems,
  homepageCollectionSliderData,
  homepageProductCarouselSliderData,
} from '@/helpers/homepage-helper';
import Banner from '@/components/Banner/index';
import { TestimonialSection } from './Homepage/Testimonial/TestimonialSection';
import { InstagramSection } from './Homepage/Instagram/InstagramSection';
import { LimitedTimeOffer } from './Homepage/LimitedTimeOffer/LimitedTimeOffer';
import { CountdownBanner } from './Homepage/CountdownBanner/CountdownBanner';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { useGetAllExpressZones, useGetAllLocationList, useMyLocation } from '@/hooks/location';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import { EXPRESS_ZONE_SENTINEL } from '@/hooks/useLocationModalQueries';
import useHeaderStore from '@/store/header';
import { useOrderStore } from '@/store/order';
import { useGetBopisOrderStatus } from '@/hooks/ordersHooks';
import { AnnouncementBar } from './Homepage/AnnouncementBar/AnnouncementBar';

const ImageSlide = ({ slide, index }: any) => {
  return (
    <>
      <div className="relative aspect-[548/955] w-full max-sm:object-cover max-sm:object-top md:aspect-[192/105]">
        <Image src={slide?.Image} alt="image" fill quality={80} placeholder="blur" blurDataURL={base64Image} unoptimized={true} />
      </div>
      <div className="absolute bottom-0 left-0 right-0 top-0 flex px-12 text-xl text-white sm:px-20 lg:px-52">
        <div className="homepage-slider-content relative flex w-full items-end p-6 max-sm:px-0">
          <div
            className="flex h-full w-full flex-col items-center text-center md:items-end md:text-right"
            dangerouslySetInnerHTML={{ __html: slide.Text }}
            data-testid={`slider-content-${index}`}
          />
        </div>
      </div>
    </>
  );
};

const HomePageComponent = ({ layoutId = '', Hash = '' }: { layoutId?: string; Hash?: string }) => {
  const authObject = useReactQuerySession();
  const pathname = usePathname();
  const { data: homePageSections } = useHomepageSections(layoutId, Hash);
  const tabData: CollectionTabProperty[] = homepageCollectionItems(homePageSections?.Collections);
  const [tabs, setTabs] = useState(tabData);
  const [collectionProductPayload, setCollectionProductPayload] = useState<IProductListPayload>({
    categoryIds: [tabs[0]?.tags],
    includeOutOfStockProducts: false,
    showDefaultImageOnly: true,
  });
  const { data: collectionData, isLoading: collectionDataLoading } = useProducts(12, 1, collectionProductPayload, 4.9 * 60 * 1000);

  const { data: memberExclusiveData, isLoading: memberExclusiveDataLoading } = useMemberExclusive(20, 1, authObject?.session);
  const memberExclusiveSlider = homepageCollectionSliderData(memberExclusiveData);
  const collectionDataSlider = homepageCollectionSliderData(collectionData);
  const productCarouselSlider = homepageProductCarouselSliderData(homePageSections?.ProductCarousel?.products);
  const productCarouselSlider2 = homepageProductCarouselSliderData(homePageSections?.ProductCarousel2?.products);
  const productCarousel = homePageSections?.ProductCarousel;
  const productCarousel2 = homePageSections?.ProductCarousel2;
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [deviceType, setDeviceType] = useState(DeviceType.Web);
  const [swiper, setSwiper] = useState<any>(null);
  const router = useRouter();
  const [openGlobalModal, setOpenGlobalModal] = useState<boolean>(false);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const { data: globalAdPopUp } = useGlobalAdPopUp();
  const setAnnouncementBarContent = useHeaderStore((state) => state.setAnnouncementBarContent);
  const setIsAnnouncementBarLoading = useHeaderStore((state) => state.setIsAnnouncementBarLoading);
  const showCorporateDiscountOption = useHeaderStore((state) => state.showCorporateDiscountOption);
  const setShowCorporateDiscountOption = useHeaderStore((state) => state.setShowCorporateDiscountOption);

  const { selectedStore, myLocation } = useDeliveryOption();
  const setMyLocation = useHeaderStore((state) => state.setMyLocation);
  const setBopisOrders = useOrderStore((state) => state.setBopisOrders);
  const { data: userCurrentLatLong } = useMyLocation();
  const { data: allLocationList } = useGetAllLocationList({
    params: {
      IncludeDivisions: true,
      IncludeDistricts: true,
      IncludeThanas: true,
      Lat: userCurrentLatLong?.latitude,
      Long: userCurrentLatLong?.longitude,
    },
  });
  const { data: bopisOrders } = useGetBopisOrderStatus();
  const setSelectedStoreIds = useHeaderStore((state) => state.setSelectedStoreIds);
  const setSelectedStoreCodes = useHeaderStore((state) => state.setSelectedStoreCodes);
  const setExpressMessage = useHeaderStore((state) => state.setExpressMessage);
  const { data: expressZones } = useGetAllExpressZones(myLocation?.value || '-1');

  const getSlidesJSX = (
    slides: [
      {
        Device: string;
        Items: ISliderProperty[];
      },
    ],
    sliderType: string,
  ) => {
    const slider = slides?.map(
      (slide) =>
        slide?.Device === deviceType && (
          <ImageSlider key={slide.Device} autoplay={{ delay: deviceType === DeviceType?.Mobile ? 4000 : 5000, pauseOnMouseEnter: false }}>
            {slide.Items?.map((item, slideIndex) => {
              return (
                <div
                  key={item?.Image + '_' + slideIndex}
                  className="flex items-center justify-center"
                  onClick={() => {
                    if (item.Url) router.push(item?.Url);
                  }}
                  style={{
                    cursor: item?.Url ? 'pointer' : 'default',
                  }}
                  data-testid={`slider-${sliderType}-item-${slideIndex}`}
                >
                  <ImageSlide slide={item} index={slideIndex} />
                </div>
              );
            })}
          </ImageSlider>
        ),
    );
    return slider;
  };

  const onTabChange = (tab: CollectionTabProperty) => {
    setActiveTab(tab);
    const payload: IProductListPayload = {
      categoryIds: [tab.tags],
      includeOutOfStockProducts: false,
      showDefaultImageOnly: true,
    };

    if (tab.seName !== HomepageSections.MemberExclusive) {
      setCollectionProductPayload(payload);
    }
  };

  const handleCloseModal = () => {
    sessionStorage.setItem('isGlobalModalClosed', 'true');
    setOpenGlobalModal(false);
  };

  const renderProductSlider = () => {
    const isLoading = (activeTab?.seName === HomepageSections.MemberExclusive && memberExclusiveDataLoading) || collectionDataLoading;

    if (isLoading) {
      return (
        <div className="min-h-[12rem] lg:min-h-[31.5rem]" data-testid="product-skeleton-loader">
          <ProductCardSkeleton />
        </div>
      );
    }

    if (activeTab?.seName === HomepageSections.MemberExclusive) {
      return (
        <AuthProductSlider
          autoPlayTimer={5000}
          products={memberExclusiveSlider}
          isCategoryProduct={true}
          showArrow={memberExclusiveSlider?.length > 0}
          setSwiper={setSwiper}
          data-testid={`auth-product-slider-${activeTab?.seName}`}
        />
      );
    }

    return (
      <ProductSlider
        autoPlayTimer={5000}
        products={collectionDataSlider}
        isCategoryProduct={true}
        showArrow={collectionDataSlider?.length > 0}
        isHomePage={true}
        setSwiper={setSwiper}
        data-testid={`product-slider-${activeTab?.seName}`}
      />
    );
  };

  useEffect(() => {
    if (bopisOrders && bopisOrders.length > 0) {
      setBopisOrders(bopisOrders);
    }
  }, [bopisOrders]);

  const hasAutoSeededRef = useRef(false);

  useEffect(() => {
    if (hasAutoSeededRef.current) {
      return;
    }
    if (allLocationList?.SelectedThanaId && Object.keys(selectedStore)?.length === 0 && !myLocation?.value) {
      const selectedThanaName = allLocationList?.Thanas?.find((thana: { Id: string }) => thana.Id === allLocationList?.SelectedThanaId)?.Name;
      setMyLocation?.({
        value: allLocationList?.SelectedThanaId,
        label: selectedThanaName as string,
      });
      hasAutoSeededRef.current = true;
    }
  }, [allLocationList?.SelectedThanaId, selectedStore, myLocation?.value, setMyLocation]);

  // Mirror of the canonical Express-zones writer so rehydrated Express
  // sessions populate zones on HomePage mount without opening the modal.
  const lastHandledZonesRef = useRef<string | null>(null);
  useEffect(() => {
    if (!expressZones) {
      return;
    }
    if (!myLocation?.value) {
      return;
    }
    if (selectedStore?.Code) {
      return;
    }
    const signature = (expressZones.Stores ?? [])
      .map((s: IStore) => s?.LocationRefcode ?? '')
      .join('|');
    if (lastHandledZonesRef.current === signature) {
      return;
    }
    lastHandledZonesRef.current = signature;

    const firstCode = expressZones.Stores?.[0]?.LocationRefcode;
    if (firstCode === EXPRESS_ZONE_SENTINEL) {
      setExpressMessage('Express delivery is not available for this location. Please select another location or choose home delivery.');
      setSelectedStoreIds([]);
      setSelectedStoreCodes(expressZones.Stores.map((s: IStore) => s.LocationRefcode));
      return;
    }
    const realStores = (expressZones.Stores ?? []).filter(
      (s: IStore) => s?.LocationRefcode && s.LocationRefcode !== EXPRESS_ZONE_SENTINEL,
    );
    setSelectedStoreIds(realStores.map((s: IStore) => s.Id));
    setSelectedStoreCodes(realStores.map((s: IStore) => s.LocationRefcode));
  }, [
    expressZones,
    myLocation?.value,
    selectedStore?.Code,
    setSelectedStoreIds,
    setSelectedStoreCodes,
    setExpressMessage,
  ]);

  useEffect(() => {
    if (globalAdPopUp && pathname === '/') {
      const isGlobalModalClosed = sessionStorage.getItem('isGlobalModalClosed');
      if (isGlobalModalClosed !== 'true') {
        setOpenGlobalModal(true);
        if (window?.innerWidth <= 768) {
          setIsMobileView(true);
        }
      }
    }
  }, [globalAdPopUp, pathname]);

  useEffect(() => {
    if (authObject.session?.accessToken && memberExclusiveData && memberExclusiveData?.PageData.length > 0) {
      let hasMemberExclusive = false;
      tabs?.find((x) => {
        if (x.seName === HomepageSections.MemberExclusive) {
          hasMemberExclusive = true;
        }
      });
      if (hasMemberExclusive == false) {
        setTabs([
          ...tabs,
          {
            label: "Member's Exclusive",
            tags: HomepageSections.MemberExclusive,
            seName: HomepageSections.MemberExclusive,
          },
        ]);
      }
    } else {
      setTabs(tabData);
    }
  }, [authObject.session, memberExclusiveData]);

  useEffect(() => {
    if (homePageSections?.Sections?.includes(HomepageSections?.AnnouncementBar)) {
      setAnnouncementBarContent(homePageSections?.AnnouncementBar?.[0] || null);
    }
    setIsAnnouncementBarLoading(false);
  }, [homePageSections?.Sections]);

  useEffect(() => {
    if (window?.innerWidth < 768) {
      setDeviceType(DeviceType.Mobile);
    } else if (window?.innerWidth >= 768) {
      setDeviceType(DeviceType.Web);
    }
  }, []);

  const KeyBannerInfo = useMemo(() => {
    return homePageSections ? getKeyBannerInfo(homePageSections?.KeyBanner, deviceType) : null;
  }, [homePageSections, deviceType]);

  useEffect(() => {
    if (showCorporateDiscountOption) {
      openNotification('success', 'Good news! You are registered under Corporate Benefits.');
      setShowCorporateDiscountOption(false);
    }
  }, [showCorporateDiscountOption]);
  return (
    <>
      {layoutId ? (
        <div className="fixed bottom-10 left-1/2 z-[9999] w-fit -translate-x-1/2 bg-red-600 px-6 py-4 text-center text-xl font-semibold text-white shadow-xl">
          Preview Mode
        </div>
      ) : (
        <></>
      )}

      {homePageSections?.Sections?.length && <CloseHomePageClientComponent homePageSections={homePageSections} />}
      {homePageSections?.Sections?.length ? (
        <div className="space-y-12 max-xs:space-y-8">
          {homePageSections?.Sections?.map((section, index) => {
            if (section === HomepageSections?.PreKeyBanner) {
              return (
                <PreBanner
                  key={section}
                  type={homePageSections?.PreKeyBanner?.[0]?.Items?.[0]?.ResourceType}
                  background={homePageSections?.PreKeyBanner?.[0]?.Items?.[0]?.ResourceUrl}
                  content={homePageSections?.PreKeyBanner?.[0]?.Items?.[0]?.Content}
                  className=""
                  altText={homePageSections?.PreKeyBanner?.[0]?.Items?.[0]?.AltText}
                  data-testid={`pre-key-banner-${index}`}
                />
              );
            } else if (section === HomepageSections?.KeyBanner) {
              return (
                <Banner
                  key={section}
                  image={KeyBannerInfo?.Image || Default_Image}
                  video={KeyBannerInfo?.Video || ''}
                  background={KeyBannerInfo?.Background}
                  bannerType={KeyBannerInfo?.BannerType!}
                  altText={KeyBannerInfo?.AltText || 'Key banner image'}
                  className={`container w-full md:mx-auto ${homePageSections?.PreKeyBanner ? '-mt-[5.5rem]' : ''}`}
                  contentClassName="aspect-[1535/720]"
                  unoptimize={true}
                  data-testid={`key-banner-${index}`}
                >
                  <div className="h-full" dangerouslySetInnerHTML={{ __html: KeyBannerInfo?.Text! }}></div>
                </Banner>
              );
            } else if (section === HomepageSections?.CategoryBrandCarousel) {
              return (
                <CategoryCarousel
                  autoPlayTimer={4000}
                  key={section}
                  carouselContent={homePageSections?.CategoryBrandCarousel}
                  className="container mx-auto w-full"
                  data-testid={`category-brand-carousel-${index}`}
                />
              );
            } else if (section === HomepageSections?.Slider) {
              return (
                <div key={section} data-testid={`slider-section-${index}`}>
                  {getSlidesJSX(homePageSections?.Slider, 'main')}
                </div>
              );
            } else if (section === HomepageSections?.ProductCarousel) {
              return (
                <Fragment key={section}>
                  {productCarouselSlider?.length > 0 && (
                    <div className={'container w-full px-14 md:mx-auto'} data-testid={`product-carousel-1-${index}`}>
                      <SpecificProductSlider
                        autoPlayTimer={4000}
                        products={productCarouselSlider}
                        title={productCarousel?.AlternateName || productCarousel?.Name}
                        seName={productCarousel?.SeName}
                        carouselType={productCarousel?.CarouselType}
                      />
                    </div>
                  )}
                </Fragment>
              );
            } else if (section === HomepageSections?.ColumnBanner) {
              return (
                <MultiColumnBanner
                  autoPlayTimer={5000}
                  key={section}
                  columnBannerItems={homePageSections?.ColumnBanner}
                  className={'container mx-auto w-full'}
                  data-testid={`column-banner-${index}`}
                />
              );
            } else if (section === HomepageSections?.Widgets) {
              return (
                <Widgets
                  key={section}
                  widgetProperties={homePageSections?.Widgets}
                  className={'container mx-auto w-full'}
                  autoPlayTimer={4000}
                  data-testid={`widgets-${index}`}
                />
              );
            } else if (section === HomepageSections?.Collections) {
              return (
                <div key={section} className={'container mx-auto w-full'} data-testid={`collections-section-${index}`}>
                  {tabs?.length > 0 && (
                    <div className="-mt-10">
                      <div className="mb-4">
                        <CustomTab
                          tabs={tabs}
                          activeTab={activeTab}
                          onTabChange={onTabChange}
                          showArrow={collectionDataSlider?.length > 0 || memberExclusiveSlider?.length > 0}
                          data-testid={`custom-tab-${index}`}
                        >
                          <CustomLink
                            prefetch={false}
                            href={
                              activeTab?.seName === HomepageSections.MemberExclusive
                                ? 'user/member-exclusive'
                                : activeTab?.queryParams
                                  ? `category/${activeTab?.seName}?${activeTab?.queryParams}`
                                  : `category/${activeTab?.seName}`
                            }
                            className="underline-custom mr-4 hidden whitespace-nowrap text-lg font-semibold uppercase tracking-widest lg:block"
                            data-testid={`view-all-link-${activeTab?.seName || 'default'}`}
                          >
                            View All
                          </CustomLink>
                          <CustomButtonGroupAsArrows swiper={swiper} data-testid={`button-group-arrows-${index}`} isTestimonial={false} />
                        </CustomTab>
                      </div>
                      <div className="">{renderProductSlider()}</div>
                      <div className={`mt-8 text-center ${collectionDataLoading ? 'hidden' : 'lg:hidden'}`}>
                        <CustomLink
                          prefetch={false}
                          href={activeTab?.seName === HomepageSections.MemberExclusive ? 'user/member-exclusive' : `category/${activeTab?.seName}`}
                          className="underline-custom text-lg font-semibold uppercase tracking-widest"
                          data-testid={`mobile-view-all-link-${activeTab?.seName || 'default'}`}
                        >
                          View All
                        </CustomLink>
                      </div>
                    </div>
                  )}
                </div>
              );
            } else if (section === HomepageSections?.Slider2) {
              return (
                <div key={section} data-testid={`slider2-section-${index}`}>
                  {getSlidesJSX(homePageSections?.Slider2, 'secondary')}
                </div>
              );
            } else if (section === HomepageSections?.ProductCarousel2) {
              return (
                <Fragment key={section}>
                  {productCarouselSlider2?.length > 0 && (
                    <div className={'container w-full px-14 md:mx-auto'} data-testid={`product-carousel-2-${index}`}>
                      <SpecificProductSlider
                        autoPlayTimer={5000}
                        products={productCarouselSlider2}
                        title={productCarousel2?.AlternateName || productCarousel2?.Name}
                        seName={productCarousel2?.SeName}
                        carouselType={productCarousel2?.CarouselType}
                      />
                    </div>
                  )}
                </Fragment>
              );
            } else if (section === HomepageSections?.Testimonial) {
              return <TestimonialSection key={section} content={homePageSections?.TestimonialSection} />;
            } else if (section === HomepageSections?.Instagram) {
              return <InstagramSection key={section} content={homePageSections?.InstagramSection} />;
            } else if (section === HomepageSections?.LimitedTimeOffer) {
              return <LimitedTimeOffer key={section} content={homePageSections?.DealsWidgets} deviceType={deviceType} />;
            } else if (section === HomepageSections?.CountdownBanner) {
              return <CountdownBanner key={section} content={homePageSections?.CountDownBanner} isHomepage />;
            } else if (section === HomepageSections.AnnouncementBar2) {
              return <AnnouncementBar key={section} content={homePageSections?.AnnouncementBar2?.[0]} position="static" isLoading={false} />;
            }
          })}
        </div>
      ) : (
        <LoadingComponent data-testid="loading-component" />
      )}
      {globalAdPopUp && globalAdPopUp?.WebPictureUrl && (
        <Popup open={openGlobalModal} closePopup={() => handleCloseModal()} data-testid="global-popup">
          <CustomLink
            href={globalAdPopUp.MediaContentUrl || ''}
            className={globalAdPopUp.MediaContentUrl ? '' : 'cursor-auto'}
            data-testid={`popup-link-${globalAdPopUp.MediaContentUrl ? 'active' : 'inactive'}`}
          >
            <img
              src={isMobileView && globalAdPopUp.MobilePictureUrl ? globalAdPopUp.MobilePictureUrl : globalAdPopUp.WebPictureUrl}
              alt="popup pic"
              className="block h-auto max-h-[calc(var(--popup-size)*1vh)] w-full !max-w-[calc(var(--popup-size)*1vw)]"
              onLoad={() => {
                setTimeout(() => {
                  handleCloseModal();
                }, 10000);
              }}
              data-testid="popup-image"
            />
          </CustomLink>
        </Popup>
      )}
    </>
  );
};
export default HomePageComponent;
